// src/Services/systemMessageService.js
import { chatService } from "./chatServices";

class SystemMessageService {
  constructor() {
    this.chatService = chatService;
  }

  // Player join/leave events
  async playerJoined(roomDocId, playerName) {
    const message = `${playerName} joined the game! 👋`;
    return this.chatService.addSystemMessage(roomDocId, "lobby", message);
  }

  async playerLeft(roomDocId, playerName) {
    const message = `${playerName} left the game 👋`;
    // Send to current phase since they might leave during any phase
    const currentPhase = "lobby"; // Could be dynamic based on game state
    return this.chatService.addSystemMessage(roomDocId, currentPhase, message);
  }

  async playerRemoved(roomDocId, playerName, hostName) {
    const message = `${playerName} was removed by ${hostName}`;
    return this.chatService.addSystemMessage(roomDocId, "lobby", message);
  }

  async hostTransferred(roomDocId, oldHostName, newHostName) {
    const message = `${newHostName} is now the host (transferred from ${oldHostName})`;
    return this.chatService.addSystemMessage(roomDocId, "lobby", message);
  }

  // Game state events
  async gameStarting(roomDocId, countdown = 10) {
    const message = `🎮 Game starting in ${countdown} seconds! Get ready!`;
    return this.chatService.addSystemMessage(roomDocId, "lobby", message);
  }

  // async gameStarted(roomDocId, roundNumber, letter) {
  //   const message = `🚀 Round ${roundNumber} started with letter "${letter}"!`;
  //   return this.chatService.addSystemMessage(roomDocId, "lobby", message);
  // }

  async speedModeActivated(roomDocId, remainingLetters) {
    const message = `⚡ SPEED MODE! Only ${remainingLetters} letters left - timer reduced to 20 seconds!`;
    return this.chatService.addSystemMessage(roomDocId, "lobby", message);
  }

  // Round transition events
  // async roundCompleted(roomDocId, roundNumber, letter) {
  //   const message = `✅ Round ${roundNumber} (letter "${letter}") completed!`;
  //   return this.chatService.addSystemMessage(roomDocId, "results", message);
  // }

  // async reviewPhaseStarted(roomDocId, roundNumber) {
  //   const message = `🔍 Review phase started for Round ${roundNumber}. Vote on answers!`;
  //   return this.chatService.addSystemMessage(roomDocId, "review", message);
  // }

  // async reviewPhaseEnded(roomDocId, roundNumber) {
  //   const message = `📊 Review completed for Round ${roundNumber}. Calculating scores...`;
  //   return this.chatService.addSystemMessage(roomDocId, "review", message);
  // }

  // async nextRoundStarting(roomDocId, nextRound, nextLetter, delay = 5) {
  //   const message = `⏳ Round ${nextRound} with letter "${nextLetter}" starting in ${delay} seconds!`;
  //   return this.chatService.addSystemMessage(roomDocId, "results", message);
  // }

  // Special round events
  // async specialRoundRevealed(roomDocId, roundNumber, specialType) {
  //   const specialMessages = {
  //     first_double: "🎯 First to submit gets DOUBLE points!",
  //     first_half: "⚠️ First to submit gets HALF points!",
  //     last_double: "🐌 Last to submit gets DOUBLE points!",
  //     last_half: "💨 Last to submit gets HALF points!",
  //     time_zones: "⏱️ Speed bonuses and penalties active!",
  //     point_thief: "🥷 Someone will steal points this round!",
  //     chosen_one: "✨ One lucky player will get TRIPLE points!",
  //     round_equality: "⚖️ Everyone gets the same score this round!",
  //     total_equality_average: "📊 All total scores will be averaged!",
  //     total_equality_random: "🎲 Everyone gets a random player's score!"
  //   };

  //   const message = `🎭 SPECIAL ROUND ${roundNumber}! ${specialMessages[specialType] || "Something special will happen!"}`;
  //   return this.chatService.addSystemMessage(roomDocId, "results", message);
  // }

  // async specialRoundEffect(roomDocId, playerName, effect) {
  //   const message = `💫 ${playerName}: ${effect}`;
  //   return this.chatService.addSystemMessage(roomDocId, "results", message);
  // }

  // Game end events
  async gameEnded(roomDocId, winnerName, totalRounds) {
    const message = `🏆 Game Over! ${winnerName} wins after ${totalRounds} rounds!`;
    return this.chatService.addSystemMessage(roomDocId, "results", message);
  }

  async gameEndedByHost(roomDocId, hostName) {
    const message = `⏹️ Game ended by host ${hostName}`;
    return this.chatService.addSystemMessage(roomDocId, "results", message);
  }

  // Room management events
  async roomSettingsChanged(roomDocId, setting, value) {
    const settingMessages = {
      enableSpecialRounds: value ? "🎲 Special rounds enabled!" : "📋 Special rounds disabled",
      categories: `🎯 Categories updated to: ${value.join(", ")}`
    };

    const message = settingMessages[setting] || `⚙️ Room settings updated`;
    return this.chatService.addSystemMessage(roomDocId, "lobby", message);
  }

  // Utility methods for complex scenarios
  async bulkSystemMessage(roomDocId, phase, messages) {
    // Send multiple system messages at once (useful for game events)
    const promises = messages.map(message =>
      this.chatService.addSystemMessage(roomDocId, phase, message)
    );

    return Promise.all(promises);
  }

  // Get appropriate emoji for different event types
  getEventEmoji(eventType) {
    const emojiMap = {
      player_join: "👋",
      player_leave: "👋",
      game_start: "🚀",
      special_round: "🎭",
      round_complete: "✅",
      game_end: "🏆",
      settings: "⚙️",
      warning: "⚠️",
      celebration: "🎉"
    };

    return emojiMap[eventType] || "ℹ️";
  }

  // Format countdown messages
  formatCountdown(seconds) {
    if (seconds <= 3) return `${seconds}... 🔥`;
    if (seconds <= 10) return `${seconds} seconds! ⏱️`;
    return `${seconds} seconds`;
  }

  // Helper to send countdown sequence
  async sendCountdownSequence(roomDocId, startSeconds = 10) {
    const countdownTimes = [10, 1];

    for (const time of countdownTimes) {
      if (time <= startSeconds) {
        setTimeout(async () => {
          const message = `🎮 Game starting in ${this.formatCountdown(time)}`;
          await this.chatService.addSystemMessage(roomDocId, "lobby", message);
        }, (startSeconds - time) * 1000);
      }
    }

    // Final "GO!" message
    setTimeout(async () => {
      const message = "🚀 GO! Game has started!";
      await this.chatService.addSystemMessage(roomDocId, "lobby", message);
    }, startSeconds * 1000);
  }
  async customTimeLimitPurchased(roomDocId, playerName, timeLimit) {
    const formatTime = (seconds) => {
      if (seconds >= 60) {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
      }
      return `${seconds}s`;
    };

    const message = {
      userId: "system",
      playerName: "NameIt!",
      type: 'system',
      content: `⏱️ ${playerName} purchased a custom timer! All rounds will now be ${formatTime(timeLimit)} instead of 45s.`,
      timestamp: new Date(),
      subtype: 'timer_upgrade',
      metadata: {
        playerName,
        timeLimit,
        cost: 50
      }
    };

    return this.chatService.addMessage(roomDocId, "lobby", message);
  }

  async gameStartedWithCustomTimer(roomDocId, firstRound, firstLetter, timeLimit) {
    const formatTime = (seconds) => {
      if (seconds >= 60) {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
      }
      return `${seconds}s`;
    };

    const message = {
      userId: "system",
      playerName: "NameIt!",
      type: 'system',
      content: `🎮 Game starting! Round ${firstRound} begins with letter "${firstLetter}" - You have ${formatTime(timeLimit)} per round!`,
      timestamp: new Date(),
      subtype: 'game_start_custom',
      metadata: {
        round: firstRound,
        letter: firstLetter,
        timeLimit,
        isCustom: true
      }
    };

    return this.chatService.addMessage(roomDocId, "lobby", message);
  }

  async gameEndedDueToPlayerLeaving(roomDocId, playerName) {
    const message = {
      userId: "system",
      playerName: "NameIt!",
      type: "system",
      content: `${playerName} left the game. Since only 2 players remained, the game has ended for everyone.`,
      timestamp: new Date(),
      systemType: "game_ended",
    };

    try {
      await this.chatService.addMessage(roomDocId, "results", message);
    } catch (error) {
      console.error("Error sending game end due to leaving message:", error);
    }
  }

  // Host forced advance from play phase
  async hostForcedAdvance(roomDocId, roundNumber, missingPlayerNames) {
    const message = {
      userId: "system",
      playerName: "NameIt!",
      type: "system",
      content: `Host moved to review phase. ${missingPlayerNames.join(", ")} ${missingPlayerNames.length > 1 ? "were" : "was"
        } missing and got empty submissions.`,
      timestamp: new Date(),
      systemType: "forced_advance",
    };

    try {
      await this.chatService.addMessage(roomDocId, "review", message);
    } catch (error) {
      console.error("Error sending forced advance message:", error);
    }
  }
}

// Export singleton instance
export const systemMessageService = new SystemMessageService();
